const _ = require("lodash");
const {cleanModeOptionsSchema} = require("./CleanModeUtil");

// These flags exist for debug purposes and never change in production.

// Lists all flags.
const flagList = [
	// If true, show admin links between school, main site, and admin pages
	"showAdminLinks",
	// If true, show the Report Bug button.
	"canReportBugs",
	// If true, allow giving up immediately.
	"giveUpTimerOff",
	// If true, can access chapters that are under construction.
	"accessUnfinishedChapters",
	// If true, can access grades that are under construction.
	"accessUnfinishedGrades",
	// If true, can access blocks that are under construction.
	"accessUnderConstructionBlocks",
	// If true, can access lab types that are under construction.
	"accessUnderConstructionLabTypes",
	// If true, can access lab blocks that are under construction.
	"accessUnderConstructionLabBlocks",
	// If true, can access review blocks (currently hidden otherwise).
	// For reference when later switching review back on: [T1723]
	"accessReview",
	// If true, can access challenge blocks (currently hidden otherwise).
	// For reference when later switching challenge back on: [T1723]
	"accessChallenge",
	// If true, can access blocks that are locked.
	"accessLockedBlocks",
	// If true, no problems in standard/puzzle sets are locked.
	"accessLockedProblems",
	// If true, bypass any test cooldowns
	"bypassTestCooldown",
	// If true, show debug navigation and problem panels.
	"showDebugPanels",
	// If true, enable the Ctrl+Alt+Cmd+0 dev testing shortcut
	"enableDevTestingShortcut",
	// If true, track all vault states in debugActionHistory, not just actions.
	"logVaultStates",
	// If true, debugActionHistory includes detailed state diffs. Slow.
	"logVaultDiffs",
	// If true, don't send errors to the vault, which forwards them to the server.
	// (Report Bug button, if enabled, will still send errors.)
	"doNotSendErrorsToServer",
	// If true, show a dancing bug and log to console when errors occur.
	"showErrorsInConsole",
	// If true, don't show messages warning about non-supported browsers
	"skipBrowserMessages",
	// If true, results will always recompute a set seed for generated blocks, even if the set seed already exists
	"alwaysRegenerateSetSeed",
	// If true, the value "test" in any checkout form field will autofill the rest with test data
	"allowTestCheckoutForms",
	// If true, right clicking the banvas starts the debugger
	"enableBanvasDebugger",
	// If true, CanvasMath will verify that all node rects are in the top one.
	"enableCanvasMathRectChecks",
	// If true, Banvas will check each view rectangle for invalid values.
	"enableBanvasViewRectChecks",
	// If true, use a proxy-based immutability check in browsers with proxies.
	"useProxyImmCheck",
	// If true, CanvasMath does not log overflowing line errors.
	"suppressCMLineOverflow",
	// Allow backend call to take as long as needed without frontend timing out.
	"disableSocketTimeout",
	// Allows book tests to ignore the time-based restriction to submitting test parts.
	"ignoreBookTestTimeRestriction",
	// If true, student tutorial is enabled
	"studentTutorialEnabled",
	// Shows Read Aloud everywhere it is available, ignoring page or BA level restrictions
	"showReadAloudEverywhereAvailable",
	// Hides Jazz and stuff to make a clean experience for Richard's videos
	"cleanMode",
	// Shows engines in "print mode" across the site
	"printMode",
	// If true, nav bar shows up yellow on eleventy pages and blue on react pages
	"navBarColorChanges",
	// If true, the check for pending sockets in RequestTracker.isDone() is skipped
	"skipSocketsCheckOnRestart",
];

// Lists all flags and maps them to their default value (i.e. value in
// production mode).
const productionValues = {
	showAdminLinks: true,
	canReportBugs: true,
	giveUpTimerOff: false,
	accessUnfinishedChapters: false,
	accessUnfinishedGrades: false,
	accessUnderConstructionBlocks: false,
	accessUnderConstructionLabTypes: false,
	accessUnderConstructionLabBlocks: false,
	accessReview: false,
	accessChallenge: false,
	accessLockedBlocks: false,
	accessLockedProblems: false,
	bypassTestCooldown: false,
	showDebugPanels: false,
	enableDevTestingShortcut: false,
	logVaultStates: false,
	logVaultDiffs: false,
	doNotSendErrorsToServer: false,
	showErrorsInConsole: false,
	skipBrowserMessages: false,
	alwaysRegenerateSetSeed: false,
	allowTestCheckoutForms: false,
	enableBanvasDebugger: false,
	enableCanvasMathRectChecks: false,
	useProxyImmCheck: false,
	suppressCMLineOverflow: false,
	disableSocketTimeout: false,
	ignoreBookTestTimeRestriction: false,

	// This can probably be false in production later, but making it true while
	// we have issues like T12788 open.
	enableBanvasViewRectChecks: true,

	studentTutorialEnabled: false,

	showReadAloudEverywhereAvailable: false,

	cleanMode: false,
	printMode: false,

	navBarColorChanges: false,

	skipSocketsCheckOnRestart: false,
};

// These flags are forced to these values in development.
const developmentValues = _.extend({}, productionValues, {
	accessUnfinishedChapters: true,
	accessUnfinishedGrades: true,
	accessUnderConstructionBlocks: true,
	showDebugPanels: true,
	enableDevTestingShortcut: true,
	logVaultStates: true,
	showErrorsInConsole: true,
	skipBrowserMessages: true,
	allowTestCheckoutForms: true,
	enableBanvasDebugger: true,
	enableBanvasViewRectChecks: true,
	studentTutorialEnabled: false,
	doNotSendErrorsToServer: true,
	showReadAloudEverywhereAvailable: true,
	navBarColorChanges: true,
	skipSocketsCheckOnRestart: true,
});

// Warnings to show in console when an option is turned on.
const warningsWhenOn = {
	logVaultDiffs: "This will slow down some browsers a lot, like old Safari.",
	useProxyImmCheck:
		"This will significantly slow down the page. " +
		"It will also break some things, like sliders.",
};

// These flags are forced to these values when there is play problem data
// set. Overrides production and development.
const playProblemDataValues = {
	canReportBugs: false,
	showDebugPanels: false,
	enableBanvasDebugger: false,
};

// Helpers

const setFlag = function(name, value) {
	if (global.PLAY_PROBLEM_DATA && playProblemDataValues[name] !== undefined) {
		value = playProblemDataValues[name];
	}
	FLAGS[name] = value;
	if (process.env.NODE_ENV !== "production" && value && warningsWhenOn[name]) {
		// eslint-disable-next-line no-console
		console.log(
			"WARNING",
			"Global flag " + name + " turned on.",
			warningsWhenOn[name]
		);
	}
};

const getSavedValue = function(storage, name, defaultValue) {
	const key = "flag_" + name;
	const storedValue = storage && storage.getItem(key);
	return storedValue ? storedValue === "true" : defaultValue;
};

// Exported

// The actual current state of the flags.
const FLAGS = {};

const setFlagAndSave = function(storage, name, value) {
	setFlag(name, value);
	if (!global.PLAY_PROBLEM_DATA && storage) {
		const key = "flag_" + name;
		// We don't remove the navBarColorChanges flag from local storage
		// because Eleventy needs to be able to read this value, and
		// Eleventy loads before the global flags actually get set.
		if (name !== "navBarColorChanges" && value === developmentValues[name]) {
			storage.removeItem(key);
		} else {
			storage.setItem(key, "" + value);
		}
	}
};

const resetAllSaved = function(storage) {
	if (storage) {
		_.each(productionValues, (value, name) => {
			const key = "flag_" + name;
			storage.removeItem(key);
		});
	}
};

const setDevelopmentEnv = function(storage) {
	flagList.forEach(name =>
		setFlagAndSave(
			storage,
			name,
			getSavedValue(storage, name, developmentValues[name])
		)
	);
};

const setProductionEnv = function() {
	flagList.forEach(name => setFlag(name, productionValues[name]));
};

// Set initial values.
setProductionEnv();

/**
 * A suboption schema item
 * @typedef {Object} SuboptionSchemaItem
 * @property {string} name - The name of the item, used in code to reference the value
 * @property {('boolean'|'number'|'text'|'color'|'enum'|'group')} type - The type of the value to get out, or alternatively the type of input control to show the user (boolean = checkbox, number and text = text field, color = color picker, enum = drop-down, group = renders as a collapsible group)
 * @property {string} [label] - The label displayed in the DevNavigation panel
 * @property {any} [default] - The default value if not set by the user
 * @property {string} [help] - Helpful information about this item
 * @property {Array.<SuboptionSchemaItem>} [items] - The items in this group if type=group
 * @property {Array.<string>} [enum] - The items that can be selected from the drop-down if type=enum
 */

/**
 * @type {Object.<Array.<SuboptionSchemaItem>>}
 */
const FLAG_OPTIONS_SCHEMA = {
	cleanMode: cleanModeOptionsSchema,
};

const makeDefaultOptionSet = optionList => {
	return optionList.reduce((accum, curr) => {
		if (curr.type === "group") {
			accum[curr.name] = makeDefaultOptionSet(curr.items);
		} else {
			accum[curr.name] = curr.default;
		}
		return accum;
	}, {});
};

const FLAG_OPTIONS = _.mapValues(FLAG_OPTIONS_SCHEMA, makeDefaultOptionSet);

module.exports = {
	FLAGS,
	getList: () => flagList,
	setFlagAndSave,
	resetAllSaved,
	setDevelopmentEnv,
	setProductionEnv,
	FLAG_OPTIONS_SCHEMA,
	FLAG_OPTIONS,
};
